import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Mail, Play } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { api } from '../../context/AuthContext';
import { DataTable, Loading, Section, StatusBadge, errorText, formatDateTime } from './shared';

/** Model health & deprecation results, open alerts, and the admin audit trail. */
const HealthTab = () => {
  const [data, setData] = useState(null);
  const [audit, setAudit] = useState([]);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    const [health, log] = await Promise.all([api.get('/admin/ai/health'), api.get('/admin/ai/audit', { params: { limit: 20 } })]);
    setData(health.data);
    setAudit(log.data.entries);
  }, []);
  useEffect(() => { load().catch(() => setData({ models: [], alerts: [] })); }, [load]);

  const runNow = async () => {
    setBusy('run');
    try {
      const res = await api.post('/admin/ai/health/run');
      toast.success(`Checked ${res.data.models} models · ${res.data.alerts_raised.length} new alert(s)`);
      await load();
    } catch (error) {
      toast.error(errorText(error, 'Health check failed'));
    } finally {
      setBusy(null);
    }
  };

  const sendTest = async () => {
    setBusy('test');
    try {
      const res = await api.post('/admin/ai/alerts/test');
      toast.success(`Test alert delivered to ${res.data.recipients.join(', ')}`
        + (res.data.failed?.length ? ` (failed: ${res.data.failed.join(', ')})` : ''));
    } catch (error) {
      toast.error(errorText(error, 'Could not send the test alert'));
    } finally {
      setBusy(null);
    }
  };

  const acknowledge = async (alertId) => {
    await api.post(`/admin/ai/alerts/${alertId}/ack`);
    load();
  };

  if (!data) return <Loading />;
  const counts = data.models.reduce((acc, m) => ({ ...acc, [m.status]: (acc[m.status] || 0) + 1 }), {});

  return (
    <div className="space-y-6">
      <Section
        title={`Open alerts${data.alerts.length ? ` (${data.alerts.length})` : ''}`}
        description="Critical alerts are emailed and posted in-app immediately; warnings go in the daily digest."
        actions={(
          <Button size="sm" variant="outline" onClick={sendTest} disabled={busy === 'test'}>
            {busy === 'test' ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Mail className="mr-1 h-4 w-4" />}Send test email
          </Button>
        )}
      >
        {data.alerts.length === 0 ? (
          <p className="py-4 text-center text-sm text-slate-500">No open alerts.</p>
        ) : (
          <ul className="-mx-6 divide-y divide-slate-100">
            {data.alerts.map((alert) => (
              <li key={alert.alert_id} className="flex flex-col gap-3 px-6 py-4 sm:flex-row sm:items-start">
                <StatusBadge status={alert.severity} />
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-slate-800">{alert.title}</p>
                  <p className="mt-0.5 break-words text-sm text-slate-600">{alert.message}</p>
                  <p className="mt-1 text-xs text-slate-400">{formatDateTime(alert.created_at)} · {alert.category}</p>
                </div>
                <Button size="sm" variant="ghost" className="self-start" onClick={() => acknowledge(alert.alert_id)}>Acknowledge</Button>
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section
        title="Model health"
        description={(
          <>
            Daily: retirement dates, provider model lists (free) and a few-token text probe.
            {data.last_run && <> Last run {formatDateTime(data.last_run.started_at)}.</>}
          </>
        )}
        actions={(
          <Button size="sm" onClick={runNow} disabled={busy === 'run'} className="bg-emerald-500 text-white hover:bg-emerald-600">
            {busy === 'run' ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Play className="mr-1 h-4 w-4" />}Run now
          </Button>
        )}
      >
        {data.models.length > 0 && (
          <div className="mb-4 flex flex-wrap gap-2">
            {Object.entries(counts).map(([status, n]) => (
              <span key={status} className="inline-flex items-center gap-1.5 text-sm text-slate-600"><StatusBadge status={status} />{n}</span>
            ))}
          </div>
        )}
        <DataTable
          rowKey={(m) => m._id}
          rows={data.models}
          empty="Not run yet. Click Run now."
          columns={[
            { key: '_id', label: 'Model', className: 'max-w-[320px] truncate font-medium text-slate-800',
              render: (m) => <span title={m._id}><span className="text-slate-500">{m.provider}/</span>{m.model}</span> },
            { key: 'status', label: 'Status', render: (m) => <StatusBadge status={m.status} /> },
            { key: 'exists', label: 'In provider list', render: (m) => (m.exists == null ? <span className="text-slate-400">n/a</span> : m.exists ? 'Yes' : 'No') },
            { key: 'probe', label: 'Probe', align: 'right', className: 'whitespace-nowrap text-slate-600',
              render: (m) => (!m.probe ? <span className="text-slate-400">-</span> : m.probe.ok ? `${m.probe.latency_ms} ms`
                : <span className="text-red-600" title={m.probe.error}>failed</span>) },
            { key: 'deprecation_date', label: 'Retires', className: 'whitespace-nowrap',
              render: (m) => (m.deprecation_date ? `${m.deprecation_date} (${m.days_left}d)` : <span className="text-slate-400">-</span>) },
            { key: 'profiles', label: 'Used by', className: 'text-slate-600', render: (m) => (m.profiles || []).join(', ') },
          ]}
        />
      </Section>

      <Section title="Audit log" description="Routing and credit-price changes by admins.">
        {audit.length === 0 ? <p className="py-4 text-center text-sm text-slate-500">No admin changes yet.</p> : (
          <DataTable
            rowKey={(e) => `${e.created_at}-${e.action}-${e.target}`}
            rows={audit}
            columns={[
              { key: 'created_at', label: 'When', className: 'whitespace-nowrap text-slate-500', render: (e) => formatDateTime(e.created_at) },
              { key: 'action', label: 'Change', render: (e) => <code className="text-xs">{e.action}</code> },
              { key: 'target', label: 'Target', className: 'font-medium text-slate-800' },
              { key: 'details', label: 'Details', className: 'text-slate-600',
                render: (e) => (e.details?.from != null ? `${e.details.from} → ${e.details.to}` : e.details?.after ? `${e.details.after.length} model chain` : '-') },
              { key: 'admin_id', label: 'By', className: 'text-xs text-slate-500' },
            ]}
          />
        )}
      </Section>
    </div>
  );
};

export default HealthTab;
